import {Component, Inject, OnInit} from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import {NgClass, NgForOf, NgIf} from '@angular/common';
import { BroadcastService } from '@services/broadcast-service';
import {EventLoggerService} from '@services/event-logger-service';

interface NutraVariant {
  objectId: string;
  cartQuantity: number;
  margUnit: number;
  quantity?: number;
  perBottle?: number;
  mrp: number;
  price: number;
  offPercentage: number;
}

interface NutraBannerData {
  title: string;
  productImage: string;
  variantData: NutraVariant[];
  unit?: string;
  packType?: string;
}

interface NutraPopupData {
  nutraBannerData: NutraBannerData;
  nutraBannerExperimentVariantData?: {
    variant?: {
      uiVariants?: {
        showProductPopup?: boolean;
      };
    };
  };
  callbacks: {
    closeBottomSheet: () => void;
    addOrRemoveNutraProductToCart: (productId: string, action: 'ADD' | 'REMOVE') => Promise<void>;
    handleProceedToBuy: (productTitle: string) => void;
  };
}

@Component({
  selector: 'nutra-popup',
  templateUrl: './nutra-popup.html',
  imports: [
    NgForOf,
    NgIf,
    NgClass,
  ],
  standalone: true
})
export class NutraPopupComponent implements OnInit {
  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: NutraPopupData,
    private readonly broadcast: BroadcastService,
    public readonly eventLogger: EventLoggerService,
    private bottomSheetRef: MatBottomSheetRef<NutraPopupComponent>
  ) { 
    // Prevent default dismissal behavior
    this.bottomSheetRef.disableClose = true;

    // Handle backdrop clicks
    this.bottomSheetRef.backdropClick().subscribe(() => {
      this.closeSheet();
    });
  }

  showProceedToBuy: boolean = false;
  activeMargUnit: number = 0;

  ngOnInit(): void {
    this.showProceedToBuy = this.data?.nutraBannerData?.variantData?.some((data) => data?.cartQuantity) || false;
  }

  async handleQuantityChange(variant: NutraVariant, action: 'ADD' | 'REMOVE'): Promise<void> {
    try {
      if (!variant) return;

      const isAdd = action === 'ADD';
      const currentQuantity = variant.cartQuantity || 0;

      // Check quantity limits
      if (isAdd && currentQuantity >= 5) return this.broadcast.broadcast('NOTIFY', { message: 'Maximum quantity is limited to 5 per product' });
      if (!isAdd && currentQuantity <= 0) return;

      // Call parent method through callbacks
      await this.data.callbacks.addOrRemoveNutraProductToCart(variant.objectId, action);

      // Only update local state if API call succeeds
      variant.cartQuantity = isAdd ? currentQuantity + 1 : currentQuantity - 1;

      // Update showProceedToBuy based on any variant having cartQuantity
      this.showProceedToBuy = this.data?.nutraBannerData?.variantData?.some((data) => data?.cartQuantity) || false;
    } catch (error) {
      console.error('Error updating cart:', error);
      // Do not update local state if API call fails
    }
  }

  setActiveMargUnit(activeMargUnit: number): void {
    this.activeMargUnit = activeMargUnit;
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'nutra_popup_product_image', margId: activeMargUnit }));
  }

  closeSheet(): void {
    this.setActiveMargUnit(0);
    this.data.callbacks.closeBottomSheet();
  }
}
